// src/layouts/MainLayout.tsx

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';

import { useNavigate, Outlet } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';

function MainLayout() {
  const { adminInfo, user, isLoggedIn, logout } = useAuthStore();
  const navigate = useNavigate();

  const isAdmin = adminInfo?.isAdmin;

  // Build a display name:
  // - For admins, try adminInfo.customerName; if not present, fallback to firstName + lastName
  // - For non-admins, use user.customerName if available
  const adminDisplayName =
    adminInfo?.customerName ||
    `${adminInfo?.firstName ?? ''} ${adminInfo?.lastName ?? ''}`.trim();

  const userDisplayName = user?.customerName || '';
  const displayName = isAdmin ? adminDisplayName : userDisplayName;

  // Logout handler
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // State for profile menu (common for desktop and mobile)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // State for mobile menu
  const [mobileAnchorEl, setMobileAnchorEl] = useState<null | HTMLElement>(null);
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileAnchorEl(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setMobileAnchorEl(null);
  };

  // Navigate to the installment settings page (for admins)
  const handleInstallmentSettings = () => {
    navigate('/installment-change');
    handleMenuClose();
    handleMobileMenuClose();
  };

  // Mobile menu component
  const renderMobileMenu = (
    <Menu
      id="mobile-menu"
      anchorEl={mobileAnchorEl}
      open={Boolean(mobileAnchorEl)}
      onClose={handleMobileMenuClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
    >
      {/* Ana Sayfa */}
      <MenuItem
        onClick={() => {
          navigate('/home');
          handleMobileMenuClose();
        }}
      >
        <ListItemIcon>
          <HomeIcon fontSize="small" />
        </ListItemIcon>
        Ana Sayfa
      </MenuItem>

      {/* Raporlar & İşlem Geçmişi for admins */}
      {isLoggedIn && (
  <MenuItem
    onClick={() => {
      navigate('/report');
      handleMobileMenuClose();
    }}
  >
    <ListItemIcon>
      <AssessmentIcon fontSize="small" />
    </ListItemIcon>
    Raporlar
  </MenuItem>
)}

{isLoggedIn && isAdmin && (
  <MenuItem
    onClick={() => {
      navigate('/log-report');
      handleMobileMenuClose();
    }}
  >
    <ListItemIcon>
      <AssessmentIcon fontSize="small" />
    </ListItemIcon>
    İşlem Geçmişi
  </MenuItem>
)}


      {/* Profile & Settings */}
      {isLoggedIn && (
        <MenuItem
          onClick={(event) => {
            // Use the same profile menu for both desktop and mobile
            handleMenuOpen(event);
            handleMobileMenuClose();
          }}
        >
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          {displayName}
        </MenuItem>
      )}

      {/* If profile menu is not used, you can also include logout directly here */}
      {isLoggedIn && (
        <MenuItem
          onClick={() => {
            handleLogout();
            handleMobileMenuClose();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Çıkış
        </MenuItem>
      )}
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Baştaş Ödeme Uygulaması
          </Typography>

          {/* Desktop Navigation: visible on md and up */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/home')}
              sx={{ mr: 2 }}
            >
              Ana Sayfa
            </Button>

            {isLoggedIn && (
  <Button
    color="inherit"
    startIcon={<AssessmentIcon />}
    onClick={() => navigate('/report')}
    sx={{ mr: 2 }}
  >
    Raporlar
  </Button>
)}

{isLoggedIn && isAdmin && (
  <Button
    color="inherit"
    startIcon={<AssessmentIcon />}
    onClick={() => navigate('/log-report')}
    sx={{ mr: 2 }}
  >
    İşlem Geçmişi
  </Button>
)}


            {isLoggedIn && (
              <Button
                color="inherit"
                onClick={handleMenuOpen}
                startIcon={<AccountCircleIcon />}
              >
                {displayName}
              </Button>
            )}
          </Box>

          {/* Mobile Navigation: visible on xs and sm */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              size="large"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Profile menu is shared between mobile and desktop */}
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            {/* Admin-only settings */}
            {isAdmin && (
              <MenuItem onClick={handleInstallmentSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Ayarlar
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Çıkış
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Render mobile menu */}
      {renderMobileMenu}

      {/* Outlet: nested routes go here */}
      <Box sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="text.secondary">
          © 2025 Duyu Payment System
        </Typography>
      </Box>
    </Box>
  );
}

export default MainLayout;
